import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  Chip
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiService from '../services/api';
import { formatBytes, formatDate } from '../utils/formatters';

const DiskVolumesPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [volumes, setVolumes] = useState([]);
  const [vms, setVms] = useState([]);
  const [volumesWithVMs, setVolumesWithVMs] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState(null);
  const [newVolume, setNewVolume] = useState({
    name: '',
    size: 10,
    format: 'qcow2',
    filesystem: '',
    mount_options: ''
  });
  const [creating, setCreating] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [volumeToDelete, setVolumeToDelete] = useState(null);

  const fetchVMs = async () => {
    try {
      const response = await apiService.getVMs(true);
      setVms(response.data.vms);
    } catch (err) {
    }
  };

  const matchVolumesWithVMs = async () => {
    const vmDetails = [];
    
    for (const vm of vms) {
      try {
        const detailResponse = await apiService.getVMInfo(vm.name);
        if (detailResponse.data && detailResponse.data.xml) {
          vmDetails.push({
            name: vm.name,
            xml: detailResponse.data.xml,
            state: vm.state,
            active: Boolean(vm.state === 1)
          });
        }
      } catch (error) {
        
      }
    }
    
    const updatedVolumes = volumes.map(volume => {
      const attachedVMs = [];
      
      vmDetails.forEach(vm => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(vm.xml, "text/xml");
        const diskElements = xmlDoc.getElementsByTagName("disk");
        
        for (let i = 0; i < diskElements.length; i++) {
          const sourceElement = diskElements[i].getElementsByTagName("source")[0];
          if (sourceElement && sourceElement.getAttribute("file")) {
            const filePath = sourceElement.getAttribute("file");
            if (filePath.includes(volume.name)) {
              const targetElement = diskElements[i].getElementsByTagName("target")[0];
              const targetDev = targetElement ? targetElement.getAttribute("dev") : "unknown";
              
              attachedVMs.push({
                name: vm.name,
                active: vm.active,
                device: targetDev,
                isRoot: targetDev === 'vda'
              });
              break;
            }
          }
        }
      });
      
      return {
        ...volume,
        attachedVMs
      };
    });
    
    setVolumesWithVMs(updatedVolumes);
  };

  const fetchVolumes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getVolumes();
      setVolumes(response.data.volumes);
    } catch (err) {
      setError('디스크 볼륨 목록을 불러오는 중 오류가 발생했습니다.');
      enqueueSnackbar('디스크 볼륨 목록을 불러오는 중 오류가 발생했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchVolumes();
      await fetchVMs();
    };
    loadData();
  }, []);

  useEffect(() => {
    if (volumes.length > 0 && vms.length > 0) {
      matchVolumesWithVMs();
    }
  }, [volumes, vms]);

  const handleRefresh = async () => {
    await fetchVolumes();
    await fetchVMs();
  };

  const handleCreateOpen = () => {
    setCreateOpen(true);
  };

  const handleCreateClose = () => {
    setCreateOpen(false);
    setNewVolume({
      name: '',
      size: 10,
      format: 'qcow2',
      filesystem: '',
      mount_options: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewVolume(prev => ({
      ...prev,
      [name]: name === 'size' ? parseInt(value, 10) || 0 : value
    }));
  };

  const handleCreateVolume = async () => {
    if (!newVolume.name) {
      enqueueSnackbar('볼륨 이름을 입력해주세요.', { variant: 'warning' });
      return;
    }

    if (newVolume.size <= 0) {
      enqueueSnackbar('볼륨 크기는 0보다 커야 합니다.', { variant: 'warning' });
      return;
    }

    setCreating(true);
    try {
      await apiService.createVolume(newVolume);
      enqueueSnackbar('디스크 볼륨이 성공적으로 생성되었습니다.', { variant: 'success' });
      handleCreateClose();
      fetchVolumes();
    } catch (err) {
      enqueueSnackbar(`디스크 볼륨 생성 중 오류가 발생했습니다: ${err.response?.data?.detail || err.message}`, { variant: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = (volume) => {
    setVolumeToDelete(volume);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
    setVolumeToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!volumeToDelete) return;
    
    try {
      await apiService.deleteVolume(volumeToDelete.name);
      enqueueSnackbar(`디스크 볼륨 ${volumeToDelete.name}이 삭제되었습니다.`, { variant: 'success' });
      fetchVolumes();
    } catch (err) {
      enqueueSnackbar(
        `디스크 볼륨 삭제 중 오류가 발생했습니다: ${err.response?.data?.detail || err.message}`, 
        { variant: 'error' }
      );
    } finally {
      handleDeleteConfirmClose();
    }
  };

  if (loading && volumes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">디스크 볼륨 관리</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateOpen}
          >
            볼륨 생성
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            새로고침
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>볼륨명</TableCell>
                <TableCell>경로</TableCell>
                <TableCell>크기</TableCell>
                <TableCell>파일시스템</TableCell>
                <TableCell>생성일</TableCell>
                <TableCell>연결된 VM</TableCell>
                <TableCell align="center">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {volumesWithVMs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      등록된 디스크 볼륨이 없습니다.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                volumesWithVMs.map((volume) => (
                  <TableRow key={volume.name} hover>
                    <TableCell>{volume.name}</TableCell>
                    <TableCell>{volume.path}</TableCell>
                    <TableCell>{formatBytes(volume.size)}</TableCell>
                    <TableCell>
                      {volume.filesystem ? (
                        <Chip 
                          label={volume.filesystem.toUpperCase()} 
                          color="success" 
                          size="small" 
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          미지정
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(volume.created)}</TableCell>
                    <TableCell>
                      {volume.attachedVMs && volume.attachedVMs.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {volume.attachedVMs.map((vm, index) => (
                            <Tooltip 
                              key={index} 
                              title={`${vm.isRoot ? '루트 디스크' : '추가 디스크'} (장치: ${vm.device})`}
                            >
                              <Chip
                                icon={<ComputerIcon />}
                                label={vm.name}
                                color={vm.isRoot ? "primary" : "default"}
                                variant={vm.active ? "filled" : "outlined"}
                                size="small"
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          연결된 VM 없음
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip 
                        title={
                          volume.attachedVMs && volume.attachedVMs.length > 0
                            ? "VM에 연결된 볼륨은 삭제할 수 없습니다"
                            : "볼륨 삭제"
                        }
                      >
                        <span>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteClick(volume)}
                            disabled={volume.attachedVMs && volume.attachedVMs.length > 0}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={createOpen} onClose={handleCreateClose}>
        <DialogTitle>디스크 볼륨 생성</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, minWidth: 400 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="볼륨 이름"
                  fullWidth
                  value={newVolume.name}
                  onChange={handleInputChange}
                  margin="normal"
                  helperText="생성할 디스크 볼륨의 이름 (확장자 제외)"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="size"
                  label="볼륨 크기 (GB)"
                  type="number"
                  fullWidth
                  value={newVolume.size}
                  onChange={handleInputChange}
                  margin="normal"
                  InputProps={{ inputProps: { min: 1 } }}
                  helperText="디스크 볼륨의 크기 (GB 단위)"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>형식</InputLabel>
                  <Select
                    name="format"
                    value={newVolume.format}
                    onChange={handleInputChange}
                    label="형식"
                  >
                    <MenuItem value="qcow2">QCOW2</MenuItem>
                    <MenuItem value="raw">RAW</MenuItem>
                    <MenuItem value="vmdk">VMDK</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>파일시스템</InputLabel>
                  <Select
                    name="filesystem"
                    value={newVolume.filesystem}
                    onChange={handleInputChange}
                    label="파일시스템"
                    disabled={newVolume.format !== 'raw'}
                  >
                    <MenuItem value="">포맷하지 않음</MenuItem>
                    <MenuItem value="ext4">ext4</MenuItem>
                    <MenuItem value="xfs">XFS</MenuItem>
                    <MenuItem value="ntfs">NTFS</MenuItem>
                    <MenuItem value="fat32">FAT32</MenuItem>
                    <MenuItem value="btrfs">Btrfs</MenuItem>
                  </Select>
                  {newVolume.format !== 'raw' && newVolume.filesystem && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                      파일시스템 포맷은 RAW 형식에서만 지원됩니다.
                    </Typography>
                  )}
                  {!newVolume.filesystem && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      파일시스템 포맷은 선택 사항입니다. VM에 마운트 후 포맷할 수도 있습니다.
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              {newVolume.filesystem === 'ext4' && newVolume.format === 'raw' && (
                <Grid item xs={12}>
                  <TextField
                    name="mount_options"
                    label="마운트 옵션"
                    fullWidth
                    value={newVolume.mount_options}
                    onChange={handleInputChange}
                    margin="normal"
                    helperText="ext4 파일시스템 마운트 옵션 (예: noatime,data=ordered)"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateClose} color="inherit" disabled={creating}>
            취소
          </Button>
          <Button
            onClick={handleCreateVolume}
            color="primary"
            variant="contained"
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : null}
          >
            {creating ? '생성 중...' : '생성'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={handleDeleteConfirmClose}>
        <DialogTitle>디스크 볼륨 삭제 확인</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {`${volumeToDelete?.name} 디스크 볼륨을 정말로 삭제하시겠습니까?`}
          </Typography>
          <Typography variant="body2" color="error">
            삭제된 볼륨은 복구할 수 없으며, 현재 VM에서 사용 중인 볼륨을 삭제하면 문제가 발생할 수 있습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose} color="inherit">
            취소
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DiskVolumesPage; 